/**
 * taskFieldHelpers
 * ----------------
 * Centralised helpers that prefer the canonical (post-Feb-2026 cleanup) field
 * names but transparently fall back to legacy ones during the FE sweep.
 *
 * Reads:   getTaskDate, getTaskStartTime, getTaskEndTime, getTaskStatusKey
 * Writes:  buildTaskWriteBody — converts a UI patch object into the canonical
 *          body shape expected by the API (task_status_key, task_local_*).
 *
 * Once the entire FE has been swept, the legacy fallbacks here can be deleted
 * and the backend compat shim retired in the same release.
 */

const LEGACY_STATUS_TO_KEY = {
  'To Do': 'open',
  'Open': 'open',
  'In Progress': 'inProgress',
  'Partial': 'partial',
  'Blocked': 'blocked',
  'Done': 'done',
  'Cancelled': 'cancelled',
  'Archived': 'archived',
  'Deleted': 'deleted',
};

const KEY_TO_LEGACY_STATUS = {
  open: 'To Do',
  inProgress: 'In Progress',
  partial: 'Partial',
  blocked: 'Blocked',
  done: 'Done',
  cancelled: 'Cancelled',
  archived: 'Archived',
  deleted: 'Deleted',
};

// ----- READ HELPERS ----------------------------------------------------------

export function getTaskDate(task) {
  return task?.task_local_date || task?.task_delivery_date || null;
}

export function getTaskStartTime(task) {
  return task?.task_local_start_time || task?.task_start_time || null;
}

export function getTaskEndTime(task) {
  return task?.task_local_end_time || task?.task_end_time || null;
}

export function getTaskStatusKey(task) {
  if (task?.task_status_key) return task.task_status_key;
  return LEGACY_STATUS_TO_KEY[task?.task_status] || 'open';
}

export function getTaskStatusLegacy(task) {
  if (task?.task_status) return task.task_status;
  return KEY_TO_LEGACY_STATUS[task?.task_status_key] || 'To Do';
}

// ----- WRITE HELPER ----------------------------------------------------------

/**
 * Convert a UI patch (with possibly legacy keys) into the canonical body
 * shape expected by POST /api/tasks and PUT /api/tasks/:id. Mirrors keys for
 * compatibility with both shapes during the transition.
 *
 * Accepts both: { task_status: "In Progress" } and { task_status_key: "inProgress" }
 *               { task_delivery_date: "2026-05-02" } and { task_local_date: "..." }
 *               { task_start_time: "09:00" } and { task_local_start_time: "..." }
 */
export function buildTaskWriteBody(patch) {
  if (!patch || typeof patch !== 'object') return {};
  const body = { ...patch };

  // status
  if (body.task_status !== undefined && body.task_status_key === undefined) {
    body.task_status_key = LEGACY_STATUS_TO_KEY[body.task_status] || 'open';
  }
  if (body.task_status_key !== undefined && body.task_status === undefined) {
    body.task_status = KEY_TO_LEGACY_STATUS[body.task_status_key] || 'To Do';
  }

  // date
  if (body.task_delivery_date !== undefined && body.task_local_date === undefined) {
    body.task_local_date = body.task_delivery_date;
  }
  if (body.task_local_date !== undefined && body.task_delivery_date === undefined) {
    body.task_delivery_date = body.task_local_date;
  }

  // start / end times
  if (body.task_start_time !== undefined && body.task_local_start_time === undefined) {
    body.task_local_start_time = body.task_start_time;
  }
  if (body.task_local_start_time !== undefined && body.task_start_time === undefined) {
    body.task_start_time = body.task_local_start_time;
  }
  if (body.task_end_time !== undefined && body.task_local_end_time === undefined) {
    body.task_local_end_time = body.task_end_time;
  }
  if (body.task_local_end_time !== undefined && body.task_end_time === undefined) {
    body.task_end_time = body.task_local_end_time;
  }

  // task_related_links: ensure list[str]
  if (typeof body.task_related_links === 'string') {
    body.task_related_links = body.task_related_links
      .split(/[\s,;]+/)
      .map((s) => s.trim())
      .filter(Boolean);
  }

  return body;
}

export const STATE_KEY_OPTIONS = ['open', 'inProgress', 'partial', 'blocked', 'done'];
export { LEGACY_STATUS_TO_KEY, KEY_TO_LEGACY_STATUS };
